import type {
  CartPaymentSession,
  PaymentInstrument,
  SelectedGateway
} from "./gateway";

/**
 * The cart/order side-effects the checkout sequence needs, injected so the
 * orchestrator stays testable with in-memory fakes. Each is pre-bound by the
 * caller (the component closes over the resolved addresses and the mutations).
 */
export interface CheckoutOps {
  /** Apply the resolved shipping/billing addresses to every cart. */
  updateAddresses(): Promise<void>;
  /**
   * The payment session ids still on record for these carts, read fresh
   * (never derived from the in-memory plan). `updateAddresses` can change a
   * cart's shipping total - a direct_integration seller's live rate is
   * re-quoted against the address on every cart update - and Medusa deletes
   * a payment collection's sessions outright whenever its amount stops
   * agreeing with the cart's (`refreshPaymentCollectionForCartWorkflow`). So
   * a session confirmed-against before `updateAddresses` can simply not
   * exist after it, and nothing about the confirm/complete calls below would
   * notice on their own - `completeCart` would be handed an id core no
   * longer has a session for, and core's own error for that
   * ("Payment sessions are required to complete cart") would reach the buyer
   * verbatim. See KAN-134.
   */
  currentPaymentSessionIds(cartIds: string[]): Promise<Set<string>>;
  /** Create the order-set that groups a multi-cart checkout; returns its id. */
  createOrderSet(): Promise<string>;
  /**
   * Complete one cart into an order; returns the created order's id.
   *
   * `paymentSessionId` is the session this cart's payment was confirmed
   * against. It is passed on to the backend, which settles that session or
   * refuses - the half of the pairing rule the client cannot enforce.
   */
  completeCart(
    cartId: string,
    paymentSessionId: string | undefined,
    orderSetId?: string
  ): Promise<string>;
}

export interface PayForCartsArgs {
  /** The initiated per-cart payment plan (client secrets already resolved). */
  sessions: CartPaymentSession[];
  /** The prepared instruments from `gateway.prepare`; one per session. */
  instruments: PaymentInstrument[];
  /**
   * The gateway to settle with, together with the provider it was built for.
   * Arrives as one value so the two cannot be supplied independently; every
   * session is checked against its `providerId` before anything is charged.
   */
  payment: SelectedGateway;
  ops: CheckoutOps;
  /** Reports each cart's lifecycle transition so the UI can render progress. */
  onProgress?: (
    cartId: string,
    status: CartPaymentSession["status"],
    error?: string
  ) => void;
  /**
   * Called as each cart becomes a real order, in order, before the next cart is
   * attempted. Exists because the sequence is fail-fast: when a later cart's
   * completion throws, the carts before it are already orders and the returned
   * `orderIds` never reaches the caller, so this is the only way it can learn
   * what did go through (analytics reports those orders - KAN-274).
   */
  onCartCompleted?: (cartId: string, orderId: string) => void;
}

function messageOf(error: unknown): string {
  return error instanceof Error && error.message ? error.message : "";
}

/**
 * The plan cannot be paid as it stands - its sessions do not belong to the
 * paying provider, or one of them cannot name the server session it was
 * initiated against.
 *
 * Distinct from a payment failure because the recovery is: nothing has been
 * charged, and a fresh initialisation produces a plan that *can* be paid. The
 * caller is expected to re-arm initialisation on it, since the buyer has no
 * way to ask for one themselves when the pilot ships a single provider.
 *
 * Note the messages below say "we are setting it up again", which is a promise
 * about what the *caller* does - true of `PaymentStep`, the only caller today.
 * A second caller either re-arms too or needs its own wording; don't let the
 * two drift.
 */
export class PaymentPlanRefusedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PaymentPlanRefusedError";
  }
}

/**
 * Execute a multi-cart checkout end to end: apply addresses -> confirm each
 * cart's payment -> group multi-cart orders under an order-set -> complete each
 * cart into an order. Returns the created order ids.
 *
 * The whole sequence lives here so its ordering guarantees hold in one testable
 * place: addresses are applied **before** anything is charged, and completion
 * happens **after** every confirm succeeds.
 *
 * It refuses outright to pay a session that was initiated against a provider
 * other than `providerId`. The gateway is chosen from `providerId`, the session
 * carries whatever provider initiated it, and nothing downstream re-reads the
 * pairing: `confirm` is handed `session.clientSecret` and trusts it belongs to
 * the gateway. So a mismatch is not a cosmetic inconsistency - a Stripe session
 * paired with the no-op manual gateway confirms nothing and then completes the
 * cart, placing an order with no money taken. Checked here rather than at the
 * call site because this is the only door to `completeCart`.
 *
 * Both sides of that comparison are client state, so it is only half the rule;
 * the other half is the backend settling the `paymentSessionId` this function
 * passes to `completeCart`, which is why a plan that cannot name one is
 * refused here too. `CONTEXT.md` -> "Provider-session pairing" is the canonical
 * account of how the halves fit together.
 *
 * Failure is **fail-fast**: on the first confirm failure it marks that session
 * `error` and throws, leaving already-confirmed sessions `confirmed` (they are
 * never reset to `ready` - that would invite a double-charge on retry). Solving
 * the partial-charge problem for real is a backend idempotency concern, tracked
 * separately.
 *
 * It also refuses if `updateAddresses` invalidated a session it was about to
 * pay - see `CheckoutOps.currentPaymentSessionIds` for why that happens and
 * KAN-134 for the checkout it broke. Checked immediately after
 * `updateAddresses` and before anything is confirmed, so - like the two
 * refusals above - nothing has been charged when it fires and resetting every
 * session for a retry is safe.
 */
export async function payForCarts({
  sessions,
  instruments,
  payment: { providerId, gateway },
  ops,
  onProgress,
  onCartCompleted
}: PayForCartsArgs): Promise<{ orderIds: string[] }> {
  if (instruments.length !== sessions.length) {
    throw new Error(
      `Expected ${sessions.length} payment instruments, got ${instruments.length}`
    );
  }

  // Refuse before any side effect. See the note above for why a mismatch here
  // is a money bug rather than an inconsistency.
  const mismatched = sessions.filter((s) => s.providerId !== providerId);
  if (mismatched.length > 0) {
    // The thrown message is what the buyer reads, so it names the recovery and
    // nothing else. The diagnosis goes to the console: reaching this line means
    // an invariant broke, and the ids are the only way to tell which way round.
    console.error(
      "[checkout] refusing to pay: payment sessions do not belong to the " +
        `selected provider "${providerId}"`,
      mismatched.map((s) => ({ cartId: s.cartId, providerId: s.providerId }))
    );
    throw new PaymentPlanRefusedError(
      "Your payment method changed while the payment was being set up. " +
        "We are setting it up again - please try again in a moment."
    );
  }

  // Refuse a plan that cannot name the session it was initiated against.
  // Unnamed, the backend falls back to completing against whatever single
  // session the cart holds - which need not be this buyer's - and a no-op
  // gateway (manual, bank transfer) confirms without a secret, so nothing else
  // in the flow would notice. See `CONTEXT.md` -> "Provider-session pairing".
  const unnamed = sessions.filter((s) => !s.paymentSessionId);
  if (unnamed.length > 0) {
    console.error(
      "[checkout] refusing to pay: payment sessions were initiated without a " +
        "server session id",
      unnamed.map((s) => ({ cartId: s.cartId, providerId: s.providerId }))
    );
    throw new PaymentPlanRefusedError(
      "We could not confirm how your payment was set up. We are setting it " +
        "up again - please try again in a moment."
    );
  }

  // 1. Apply the resolved addresses to every cart before charging anything.
  await ops.updateAddresses();

  // Re-check every session survived that. See `CheckoutOps.currentPaymentSessionIds`
  // for why an address update can delete one out from under this plan - caught
  // here, before the confirm loop, so a vanished session is refused instead of
  // being confirmed against a client secret core no longer holds (Stripe) or
  // completed against an id core no longer has a session for (everyone else).
  const currentSessionIds = await ops.currentPaymentSessionIds(
    Array.from(new Set(sessions.map((s) => s.cartId)))
  );
  const vanished = sessions.filter(
    (s) => !s.paymentSessionId || !currentSessionIds.has(s.paymentSessionId)
  );
  if (vanished.length > 0) {
    console.error(
      "[checkout] refusing to pay: payment session(s) no longer exist after " +
        "addresses were applied - the cart total likely changed",
      vanished.map((s) => ({
        cartId: s.cartId,
        paymentSessionId: s.paymentSessionId
      }))
    );
    throw new PaymentPlanRefusedError(
      "Your order total changed while we were setting up payment. We are " +
        "setting it up again - please try again in a moment."
    );
  }

  // 2. Confirm each cart's payment in turn (manual gateways no-op).
  for (let i = 0; i < sessions.length; i++) {
    const session = sessions[i];
    onProgress?.(session.cartId, "confirming");
    try {
      await gateway.confirm(session.clientSecret, instruments[i]);
    } catch (error) {
      const message =
        messageOf(error) || `Payment failed for ${session.sellerName}`;
      onProgress?.(session.cartId, "error", message);
      // Rethrown as it came (a `PaymentFailedError` stays one) so the caller can
      // tell a decline from an unexpected fault.
      throw error instanceof Error && error.message
        ? error
        : new Error(message);
    }
    onProgress?.(session.cartId, "confirmed");
  }

  // 3. Group multi-cart orders under one order-set.
  const orderSetId =
    sessions.length > 1 ? await ops.createOrderSet() : undefined;

  // 4. Complete every cart into an order.
  const orderIds: string[] = [];
  for (const session of sessions) {
    const orderId = await ops.completeCart(
      session.cartId,
      session.paymentSessionId,
      orderSetId
    );
    orderIds.push(orderId);
    onCartCompleted?.(session.cartId, orderId);
  }

  return { orderIds };
}
